import * as Sentry from "@sentry/react";

/**
 * The ids that identify "what the user was looking at" for Sentry.
 *
 * Every field is optional and merged into a module-level record, because the
 * slices come from different places in the tree: the profile knows the tenant,
 * the agent surface knows the conversation and message, the chat drawer knows
 * the assistant conversation. Passing `null` clears a field.
 */
export interface LytraSentryContext {
  tenantId?: string | null;
  /** Email/ticket conversation. */
  conversationId?: string | null;
  /**
   * Lytra `Message` UUID, the same value jupiter tags on backend events. Never an
   * external id (internet message id, ticket number): those do not join up.
   */
  messageId?: string | null;
  /** Assistant chat conversation. */
  chatConversationId?: string | null;
  /** Which surface the ids came from, e.g. "email", "ticket", "venus-agent". */
  surface?: string | null;
  /**
   * The user's installed browser-extension version, passed in by the Neptune
   * shell (#650). Tagged as `neptune_version` to match the tag jupiter sets from
   * the X-Neptune-Version header, so frontend and backend events join up.
   */
  extensionVersion?: string | null;
}

const current: LytraSentryContext = {};

function apply(): void {
  const { tenantId, conversationId, messageId, chatConversationId, surface, extensionVersion } =
    current;

  // Tags are the searchable half (`conversation_id:<uuid>` in Sentry search).
  // snake_case matches the tags jupiter sets on backend events.
  //
  // Both conversation ids double as Langfuse session ids — jupiter traces every
  // pipeline with session_id = conversation id (jupiter/ai/observability.py) —
  // so pasting one into Langfuse's session search finds the run. No Langfuse URL
  // is built here on purpose: that would put the project id in a public bundle.
  Sentry.setTag("tenant_id", tenantId ?? undefined);
  Sentry.setTag("conversation_id", conversationId ?? undefined);
  Sentry.setTag("message_id", messageId ?? undefined);
  Sentry.setTag("chat_conversation_id", chatConversationId ?? undefined);
  Sentry.setTag("neptune_version", extensionVersion ?? undefined);

  const context: Record<string, string> = {};
  if (tenantId) context.tenant_id = tenantId;
  if (conversationId) context.conversation_id = conversationId;
  if (messageId) context.message_id = messageId;
  if (chatConversationId) context.chat_conversation_id = chatConversationId;
  if (surface) context.surface = surface;

  Sentry.setContext("lytra", Object.keys(context).length > 0 ? context : null);
}

/**
 * Merge ids into the Sentry scope so errors AND user feedback carry them.
 *
 * The feedback widget snapshots the active scope on submit, so whatever is set
 * here lands on the feedback entry without the reporter having to describe it.
 * The user is not set here — both apps already call `Sentry.setUser`, which
 * Sentry indexes as `user.id`.
 */
export function setLytraSentryContext(patch: LytraSentryContext): void {
  Object.assign(current, patch);
  apply();
}

/** The ids currently on the Sentry scope, e.g. to copy a feedback report elsewhere. */
export function getLytraSentryContext(): Readonly<LytraSentryContext> {
  return { ...current };
}
